import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { type LinkProps, useNavigate, useSearch } from '@tanstack/react-router'
import { type SubmitEvent, useState } from 'react'

import type { AccountIdentity } from '@/bindings/AccountIdentity'
import type { AccountLookup } from '@/bindings/AccountLookup'
import type { AccountLookupRequest } from '@/bindings/AccountLookupRequest'
import type { BranchRef } from '@/bindings/BranchRef'
import type { GrantRoleRequest } from '@/bindings/GrantRoleRequest'
import type { RestaurantAccount } from '@/bindings/RestaurantAccount'
import type { RestaurantRole } from '@/bindings/RestaurantRole'
import type { RoleAssignment } from '@/bindings/RoleAssignment'
import { Badge } from '@/components/badge'
import { Button } from '@/components/button'
import { Checkbox, Field, Select } from '@/components/field'
import { FormError } from '@/components/form-error'
import { Card, Page } from '@/components/layout'
import { emptyIdentifierDraft, toLoginIdentifier } from '@/features/auth/identifier-draft'
import { IdentifierSearch } from '@/features/auth/identifier-search'
import { Identity, RevokeButton } from '@/features/back-office/account-parts'
import {
  accountListQuery,
  accountRolePath,
  backOfficeKeys,
  restaurantBranchesQuery,
  restaurantPath,
} from '@/features/back-office/api'
import { request } from '@/lib/api'
import { Pager } from '@/components/pager'
import { useManagedRestaurant } from '@/features/back-office/scope'
import { useT } from '@/lib/i18n/translate'
import { useClampedPage } from '@/lib/paging'
import { ROLE_LABELS, canManageRestaurant, canManageRole, isBranchScoped, manageableRoles, nextLowerRole } from '@/lib/roles'

/** The matched route id (for params/search) vs. the URL the links target. */
const ROUTE_ID = '/$restaurantSlug/manage/accounts'
const ROUTE_PATH = '/$restaurantSlug/manage/accounts'

/**
 * Back-office accounts page (PRD, E7): the roster filtered by one manageable
 * role (next-lower by default), exact phone/email lookup of any account, and
 * grant/revoke — all under the strictly-higher rule the backend enforces.
 */
export function RestaurantAccountsPage() {
  const t = useT()
  const { restaurantId, context } = useManagedRestaurant()
  const search = useSearch({ from: ROUTE_ID })

  const actor = context.role
  const fallback = nextLowerRole(actor)
  if (!canManageRestaurant(actor) || !fallback) {
    return (
      <Page width="md" className="flex flex-col gap-4">
        <h1 className="text-xl font-semibold">{t('accounts.title')}</h1>
        <p role="alert" className="text-muted text-sm">
          {t('accounts.managerOnly')}
        </p>
      </Page>
    )
  }
  // A typed `?role=` above the actor's rank silently becomes the default.
  const role = search.role && canManageRole(actor, search.role) ? search.role : fallback

  return (
    <Page width="md" className="flex flex-col gap-4">
      <h1 className="text-xl font-semibold">{t('accounts.title')}</h1>
      <LookupCard restaurantId={restaurantId} actor={actor} />
      <RosterCard restaurantId={restaurantId} actor={actor} role={role} page={search.page} />
    </Page>
  )
}

/** The grant flow's search: the login form's Email | Phone toggle, exact match (T11.3). */
function LookupCard({ restaurantId, actor }: { restaurantId: string; actor: RestaurantRole }) {
  const t = useT()
  const [open, setOpen] = useState(false)
  const [draft, setDraft] = useState(() => emptyIdentifierDraft())
  const [found, setFound] = useState<AccountLookup | undefined>()
  const [status, setStatus] = useState<'saved' | 'revoked' | undefined>()

  const lookup = useMutation({
    mutationFn: () => {
      const body: AccountLookupRequest = { identifier: toLoginIdentifier(draft) }
      return request<AccountLookup>(restaurantPath(restaurantId, '/accounts/lookup'), { method: 'POST', body })
    },
    onSuccess: (result) => {
      setStatus(undefined)
      setFound(result)
    },
  })

  function search() {
    setFound(undefined)
    lookup.mutate()
  }

  if (!open) {
    return (
      <div className="flex justify-end">
        <Button variant="secondary" onClick={() => setOpen(true)}>
          {t('accounts.find')}
        </Button>
      </div>
    )
  }

  return (
    <Card className="flex flex-col gap-4">
      <div className="flex items-center justify-between gap-2">
        <h2 className="text-lg font-semibold">{t('accounts.find')}</h2>
        <Button size="sm" variant="ghost" onClick={() => setOpen(false)}>
          {t('common.cancel')}
        </Button>
      </div>
      <div className="flex flex-col gap-4">
        <p className="text-muted text-sm">{t('accounts.findIntro')}</p>
        <IdentifierSearch
          draft={draft}
          onDraftChange={(next) => {
            setDraft(next)
            if (lookup.error) lookup.reset()
          }}
          onSearch={search}
          pending={lookup.isPending}
          error={lookup.error}
        />
      </div>
      {found && (
        <div className="border-border flex flex-col gap-4 border-t pt-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <Identity user={found.user} />
            {found.assignment ? (
              <Badge tone="success">{t(ROLE_LABELS[found.assignment.role])}</Badge>
            ) : (
              <Badge tone="muted">{t('accounts.noRole')}</Badge>
            )}
          </div>
          {status && (
            <p role="status" className="text-muted text-sm">
              {t(status === 'saved' ? 'accounts.saved' : 'accounts.revoked')}
            </p>
          )}
          <RoleForm
            key={found.user.id + (found.assignment?.role ?? '')}
            restaurantId={restaurantId}
            actor={actor}
            user={found.user}
            current={found.assignment}
            onSaved={(saved) => {
              setFound({ user: saved.user, assignment: saved.assignment })
              setStatus('saved')
            }}
          />
          {found.assignment && (
            <RevokeButton
              path={accountRolePath(restaurantId, found.user.id)}
              queryKey={backOfficeKeys.accounts(restaurantId)}
              user={found.user}
              label="accounts.revoke"
              confirm="accounts.confirmRevoke"
              confirmOf="accounts.confirmRevokeOf"
              onRevoked={() => {
                setFound({ user: found.user, assignment: null })
                setStatus('revoked')
              }}
            />
          )}
        </div>
      )}
    </Card>
  )
}

/**
 * Role + branch scope for one account; PUT replaces the whole assignment.
 * The manageable options are the actor's (`manageableRoles`), never higher.
 */
function RoleForm({
  restaurantId,
  actor,
  user,
  current,
  onSaved,
  onCancel,
}: {
  restaurantId: string
  actor: RestaurantRole
  user: AccountIdentity
  current: RoleAssignment | null
  onSaved: (saved: RestaurantAccount) => void
  onCancel?: () => void
}) {
  const t = useT()
  const queryClient = useQueryClient()
  const options = manageableRoles(actor)
  const branches = useQuery(restaurantBranchesQuery(restaurantId))
  const [role, setRole] = useState<RestaurantRole>(current?.role ?? options[0]!)
  const [branchIds, setBranchIds] = useState<readonly string[]>(() => current?.branches.map((b) => b.id) ?? [])
  const [missingBranch, setMissingBranch] = useState(false)
  const scoped = isBranchScoped(role)

  const save = useMutation({
    mutationFn: () => {
      const body: GrantRoleRequest = { role, branch_ids: scoped ? [...branchIds] : [] }
      return request<RestaurantAccount>(accountRolePath(restaurantId, user.id), { method: 'PUT', body })
    },
    onSuccess: async (saved) => {
      await queryClient.invalidateQueries({ queryKey: backOfficeKeys.accounts(restaurantId) })
      onSaved(saved)
    },
  })

  function submit(event: SubmitEvent) {
    event.preventDefault()
    // The backend refuses too (BRANCH_REQUIRED); catching it here spares a round trip.
    if (scoped && branchIds.length === 0) {
      setMissingBranch(true)
      return
    }
    save.mutate()
  }

  function toggleBranch(id: string, on: boolean) {
    setMissingBranch(false)
    setBranchIds((ids) => (on ? [...ids, id] : ids.filter((other) => other !== id)))
  }

  const unchanged =
    current !== null &&
    current.role === role &&
    (!scoped || sameSet(current.branches.map((b) => b.id), branchIds))

  return (
    <form onSubmit={submit} className="flex flex-col gap-4">
      <Field label={t('accounts.role')}>
        <Select
          value={role}
          onChange={(e) => {
            setMissingBranch(false)
            setRole(e.target.value as RestaurantRole)
          }}
        >
          {options.map((option) => (
            <option key={option} value={option}>
              {t(ROLE_LABELS[option])}
            </option>
          ))}
        </Select>
      </Field>
      {scoped ? (
        <fieldset className="flex flex-col gap-1">
          <legend className="mb-1 text-sm font-medium">{t('accounts.branches')}</legend>
          {branches.data ? (
            branches.data.items.map((branch) => (
              <Checkbox
                key={branch.id}
                label={<BranchLabel branch={branch} />}
                checked={branchIds.includes(branch.id)}
                onChange={(e) => toggleBranch(branch.id, e.target.checked)}
              />
            ))
          ) : (
            <p className="text-muted text-sm">{t('common.loading')}</p>
          )}
          {missingBranch && (
            <p role="alert" className="text-danger text-sm">
              {t('error.BRANCH_REQUIRED')}
            </p>
          )}
          <p className="text-muted text-sm">{t('accounts.branchesHint')}</p>
        </fieldset>
      ) : (
        <p className="text-muted text-sm">{t('accounts.restaurantWide')}</p>
      )}
      {current?.role !== role && <p className="text-muted text-sm">{t('accounts.grantNotice')}</p>}
      <FormError error={save.error} />
      <div className="flex gap-2">
        <Button type="submit" loading={save.isPending} disabled={unchanged}>
          {t(current ? 'common.save' : 'accounts.grant')}
        </Button>
        {onCancel && (
          <Button type="button" variant="ghost" onClick={onCancel}>
            {t('common.cancel')}
          </Button>
        )}
      </div>
    </form>
  )
}

function sameSet(a: readonly string[], b: readonly string[]): boolean {
  return a.length === b.length && a.every((id) => b.includes(id))
}

function BranchLabel({ branch }: { branch: BranchRef }) {
  const t = useT()
  return (
    <span className="flex flex-wrap items-center gap-2">
      {branch.name}
      {branch.deactivated_at && <Badge tone="muted">{t('restaurants.deactivated')}</Badge>}
    </span>
  )
}

function RosterCard({
  restaurantId,
  actor,
  role,
  page,
}: {
  restaurantId: string
  actor: RestaurantRole
  role: RestaurantRole
  page: number
}) {
  const t = useT()
  const navigate = useNavigate()
  const list = useQuery(accountListQuery(restaurantId, role, page))
  const { restaurantSlug } = useManagedRestaurant()
  const params = { restaurantSlug }
  const link = (page: number): LinkProps => ({ to: ROUTE_PATH, params, search: { page, role } })
  useClampedPage(page, list.data, link)

  const total = list.data?.total ?? 0

  return (
    <Card className="flex flex-col gap-4">
      <Field label={t('accounts.roleFilter')} className="sm:max-w-xs">
        <Select
          value={role}
          onChange={(e) =>
            void navigate({ to: ROUTE_PATH, params, search: { page: 1, role: e.target.value as RestaurantRole } })
          }
        >
          {manageableRoles(actor).map((option) => (
            <option key={option} value={option}>
              {t(ROLE_LABELS[option])}
            </option>
          ))}
        </Select>
      </Field>

      {list.isPending && <p className="text-muted text-sm">{t('common.loading')}</p>}
      {list.error && (
        <div className="flex flex-col gap-2">
          <FormError error={list.error} />
          <Button variant="secondary" onClick={() => void list.refetch()} className="self-start">
            {t('common.retry')}
          </Button>
        </div>
      )}
      {list.data &&
        (list.data.items.length === 0 ? (
          <p className="text-muted text-sm">{t('accounts.empty')}</p>
        ) : (
          <ul className="flex flex-col gap-2">
            {list.data.items.map((account) => (
              <li key={account.user.id}>
                <AccountRow restaurantId={restaurantId} actor={actor} account={account} />
              </li>
            ))}
          </ul>
        ))}

      <Pager
        label={t('accounts.pager')}
        page={page}
        total={total}
        link={link}
      />
    </Card>
  )
}


function AccountRow({
  restaurantId,
  actor,
  account,
}: {
  restaurantId: string
  actor: RestaurantRole
  account: RestaurantAccount
}) {
  const t = useT()
  const [editing, setEditing] = useState(false)
  const { user, assignment } = account

  return (
    <div className="border-border flex flex-col gap-3 rounded-lg border p-3">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <Identity user={user} />
        {!editing && (
          <div className="flex items-center gap-2">
            <Button size="sm" variant="secondary" onClick={() => setEditing(true)}>
              {t('accounts.edit')}
            </Button>
            <RevokeButton
              path={accountRolePath(restaurantId, user.id)}
              queryKey={backOfficeKeys.accounts(restaurantId)}
              user={user}
              label="accounts.revoke"
              confirm="accounts.confirmRevoke"
              confirmOf="accounts.confirmRevokeOf"
              onRevoked={() => setEditing(false)}
            />
          </div>
        )}
      </div>
      {isBranchScoped(assignment.role) && !editing && (
        <p className="text-muted flex flex-wrap gap-x-3 text-sm">
          {assignment.branches.map((branch) => (
            <span key={branch.id}>{branch.name}</span>
          ))}
        </p>
      )}
      {editing && (
        <div className="border-border border-t pt-3">
          <RoleForm
            restaurantId={restaurantId}
            actor={actor}
            user={user}
            current={assignment}
            onSaved={() => setEditing(false)}
            onCancel={() => setEditing(false)}
          />
        </div>
      )}
    </div>
  )
}
